"use client";

import { useEffect, useRef } from "react";

import Icon from "@/components/ui/AppIcon";

const shareRoles = [
  {
    role: "Family member",
    desc: "Full access — location, health records, reminders.",
    icon: "HomeIcon",
    color: "#5EBFB3",
    bg: "#E8F7F6",
    example: "Your partner or parents",
  },
  {
    role: "Pet sitter",
    desc: "Location, care notes, emergency contacts only.",
    icon: "UserIcon",
    color: "#7C3AED",
    bg: "#EDE9FE",
    example: "Rover sitter, neighbor",
  },
  {
    role: "Veterinarian",
    desc: "Full health records, vaccine history, medications.",
    icon: "BeakerIcon",
    color: "#0369A1",
    bg: "#E0F2FE",
    example: "Your primary vet clinic",
  },
  {
    role: "Groomer / Trainer",
    desc: "Care notes, allergies, behavioral history.",
    icon: "ScissorsIcon",
    color: "#D97706",
    bg: "#FEF3C7",
    example: "Your regular groomer",
  },
];

export default function TrustedCircleFeature() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up, .scroll-scale").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 90);
            });
          }
        });
      },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white dark:bg-[#0f172a]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-14 scroll-fade-up">
          <span className="teal-badge mb-4">Trusted Circle</span>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-[#1A2B2B] dark:text-slate-100 tracking-tight mt-4 mb-4">
            Share the right info
            <br />
            <span className="gradient-text">with the right people.</span>
          </h2>
          <p className="text-[#4A6363] dark:text-slate-400 text-lg max-w-xl mx-auto leading-relaxed">
            You decide exactly what each person can see. Family gets everything. The groomer gets just what they need. Nobody sees more than you allow.
          </p>
        </div>

        {/* Role cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {shareRoles.map((role, idx) => (
            <div
              key={role.role}
              className={`scroll-scale stagger-${idx + 1} feature-card p-6 flex flex-col gap-4 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-xl`}
            >
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center"
                style={{ background: role.bg }}
              >
                <Icon name={role.icon as "HomeIcon"} size={22} style={{ color: role.color }} />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#1A2B2B] dark:text-slate-100 mb-1.5 font-heading">{role.role}</h3>
                <p className="text-sm text-[#4A6363] dark:text-slate-400 leading-relaxed">{role.desc}</p>
              </div>
              <div
                className="mt-auto text-[11px] font-semibold px-3 py-1.5 rounded-full"
                style={{ background: role.bg, color: role.color }}
              >
                e.g. {role.example}
              </div>
            </div>
          ))}
        </div>

        {/* Privacy callout */}
        <div
          className="scroll-fade-up rounded-3xl p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-6 dark:bg-slate-800"
          style={{ background: "linear-gradient(135deg, #E8F7F6 0%, #EEF2FF 100%)" }}
        >
          <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-card">
            <Icon name="LockClosedIcon" size={24} className="text-teal-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-[#1A2B2B] dark:text-slate-100 mb-2 font-heading">Your data, your rules.</h3>
            <p className="text-[#4A6363] dark:text-slate-400 leading-relaxed">
              DogFob never sells your pet's data. Sharing is always opt-in, revocable at any time, and controlled entirely by you. Every access is logged so you always know who saw what.
            </p>
          </div>
          <div className="flex-shrink-0">
            <span className="teal-badge">SOC 2 compliant</span>
          </div>
        </div>
      </div>
    </section>
  );
}